import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ColumnFilterPanel from '@/components/CustomizableTable/ColumnFilterPanel';
import ColumnFilterControls from '@/components/CustomizableTable/ColumnFilterControls';
import {
    dispatch_integration_types,
    middleMileOptions,
    order_types,
    scheduledOptions,
    volumeOptions,
    wh_statuses,
} from './filterOptions';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import { useClientUser } from '@/hooks';
import { MILESTONE_OPTIONS } from '@onward-delivery/core';
import { decodeListParam, encodeListParam, withPickedBinOptions } from './utils';

export const COLUMN_FILTER_MAP = {
    created_at: {
        title: 'Order Creation Date',
        controls: [{ type: 'dateRange', keys: ['startDate', 'endDate'], label: 'Created Between' }],
    },
    deldate: {
        title: 'Scheduled Delivery Date',
        controls: [
            { type: 'dateRange', keys: ['schedDelStart', 'schedDelEnd'], label: 'Scheduled Between' },
            { type: 'select', key: 'scheduledStatus', label: 'Scheduled Status', options: scheduledOptions },
        ],
    },
    est_ship_date: {
        title: 'Estimated Shipment Date',
        controls: [{ type: 'dateRange', keys: ['estShipStart', 'estShipEnd'], label: 'Est. Ship Between' }],
    },
    est_received_date: {
        title: 'Estimated Warehouse Received Date',
        controls: [
            { type: 'dateRange', keys: ['warehouseStartDate', 'warehouseEndDate'], label: 'Est. Received Between' },
        ],
    },
    received_date: {
        title: 'Actual Received Date',
        controls: [
            { type: 'dateRange', keys: ['actualReceivedStart', 'actualReceivedEnd'], label: 'Received Between' },
        ],
    },
    warehousestatus: {
        title: 'Warehouse Status',
        controls: [{ type: 'select', key: 'warehouseStatus', label: 'Warehouse Status', options: wh_statuses }],
    },
    manufacturer: {
        title: 'Manufacturer',
        controls: [{ type: 'select', key: 'manufacturer', label: 'Manufacturer', dynamicOptions: 'manufacturers' }],
    },
    order_type: {
        title: 'Order Type',
        controls: [{ type: 'select', key: 'orderType', label: 'Order Type', options: order_types }],
    },
    service_type: {
        title: 'Service Type',
        controls: [{ type: 'serviceType', key: 'serviceType', label: 'Service Type' }],
    },
    shipper: {
        title: 'Shipper',
        controls: [{ type: 'select', key: 'shipper', label: 'Shipper', dynamicOptions: 'shippers' }],
    },
    carrier: {
        title: 'Agent',
        controls: [{ type: 'select', key: 'carrier', label: 'Agent', dynamicOptions: 'carriers' }],
    },
    dispatch_integration_type: {
        title: 'Agent TMS',
        controls: [
            { type: 'select', key: 'dispatchIntegrationType', label: 'Agent TMS', options: dispatch_integration_types },
        ],
    },
    middle_mile_integration_status: {
        title: 'Middle Mile Status',
        controls: [
            { type: 'select', key: 'middleMileStatus', label: 'Middle Mile Status', options: middleMileOptions },
        ],
    },
    vol: {
        title: 'Cubic Feet',
        controls: [{ type: 'select', key: 'volume', label: 'Cubic Feet', options: volumeOptions }],
    },
    milestone: {
        title: 'Order Status',
        controls: [
            {
                type: 'select',
                key: 'milestone',
                label: 'Order Status',
                options: MILESTONE_OPTIONS.sort((a, b) => a.value.localeCompare(b.value)).map((o) => ({
                    value: o.value,
                    label: o.value,
                })),
            },
        ],
    },
    bin_pool_location: {
        title: 'Bin/Pool Location',
        controls: [
            {
                type: 'multiAutocomplete',
                key: 'binLocations',
                label: 'Bin/Pool Location',
                dynamicOptions: 'binLocations',
                noOptionsText: 'No matching bins or pools. You can also type a bin or pool name in the search bar.',
            },
        ],
    },
    assigned_admin: {
        title: 'Assigned Admin',
        controls: [
            {
                type: 'select',
                key: 'assignedAdminId',
                label: 'Assigned Admin',
                dynamicOptions: 'onward_admins',
            },
        ],
    },
};

const MyOrdersColumnFilterPanel = ({
    open,
    columnId,
    onClose,
    applyFilters,
    searchParams,
    shippers = [],
    carriers = [],
    manufacturersList = [],
    onwardAdminOptions = [],
    binLocationOptions = [],
}) => {
    const { service_levels } = useClientUser();

    const serviceTypes = useMemo(() => {
        return [
            ...Object.entries(LOCATION_TYPES).map(([value, label]) => ({ label, value })),
            ...service_levels.map(({ service_level_id, service_level }) => ({
                label: service_level,
                value: service_level_id,
            })),
        ];
    }, [service_levels]);

    const [localFilters, setLocalFilters] = useState({});

    const config = COLUMN_FILTER_MAP[columnId];

    useEffect(() => {
        if (!open || !config) return;

        const next = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange' && Array.isArray(control.keys)) {
                control.keys.forEach((key) => {
                    next[key] = searchParams.get(key) || '';
                });
            } else if (control.type === 'select' && control.key) {
                next[control.key] = searchParams.get(control.key) || '';
            } else if (control.type === 'multiAutocomplete' && control.key) {
                next[control.key] = decodeListParam(searchParams.get(control.key));
            } else if (control.type === 'serviceType') {
                next.serviceType = searchParams.get('serviceType') || '';
                next.locationType = searchParams.get('locationType') || '';
            }
        });

        setLocalFilters(next);
    }, [open, config, searchParams]);

    const setValue = (key, value) => {
        setLocalFilters((prev) => ({ ...prev, [key]: value }));
    };

    const resolveOptions = useCallback(
        (control) => {
            if (control.type === 'serviceType') return serviceTypes;
            if (control.dynamicOptions === 'binLocations') {
                return withPickedBinOptions(binLocationOptions, localFilters[control.key]);
            }
            if (control.type === 'select') {
                if (control.dynamicOptions === 'shippers') return [{ value: 'All', label: 'All' }, ...shippers];
                if (control.dynamicOptions === 'carriers') return [{ value: 'All', label: 'All' }, ...carriers];
                if (control.dynamicOptions === 'manufacturers') return manufacturersList;
                if (control.dynamicOptions === 'onward_admins') return onwardAdminOptions;
                return control.options || [];
            }
            return control.options || [];
        },
        [manufacturersList, serviceTypes, shippers, onwardAdminOptions, binLocationOptions, localFilters]
    );

    if (!config) return null;

    const handleApply = () => {
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    payload[key] = localFilters[key] || null;
                });
            } else if (control.type === 'select') {
                payload[control.key] = localFilters[control.key] || null;
            } else if (control.type === 'multiAutocomplete') {
                payload[control.key] = encodeListParam(localFilters[control.key]);
            } else if (control.type === 'serviceType') {
                payload.serviceType = localFilters.serviceType || null;
                payload.locationType = localFilters.locationType || null;
            }
        });

        applyFilters(payload);
    };

    const handleClear = () => {
        const payload = {};
        config.controls.forEach((control) => {
            if (control.type === 'dateRange') {
                control.keys.forEach((key) => {
                    payload[key] = null;
                });
            } else if (control.type === 'select' || control.type === 'multiAutocomplete') {
                payload[control.key] = null;
            } else if (control.type === 'serviceType') {
                payload.serviceType = null;
                payload.locationType = null;
            }
        });
        setLocalFilters((prev) => {
            const cleared = { ...prev };
            Object.keys(payload).forEach((key) => {
                cleared[key] = '';
            });
            return cleared;
        });

        applyFilters(payload);
    };

    return (
        <ColumnFilterPanel
            open={open}
            columnId={columnId}
            title={config.title}
            onClose={onClose}
            onApply={handleApply}
            onClear={handleClear}
            topOffset={50}
        >
            <ColumnFilterControls
                controls={config.controls}
                values={localFilters}
                setValue={setValue}
                resolveOptions={resolveOptions}
                locationTypesMap={LOCATION_TYPES}
            />
        </ColumnFilterPanel>
    );
};

export default MyOrdersColumnFilterPanel;
